// 画面のあちこちで書きかえる値（2026-10-21 に app.js から分けた。モジュールの外からは代入できないので、ここの S にまとめて S.名前 で読み書きする）。
// キャラそのもの（データ・カテゴリ・状態・名前・柄）は 2026-10-23 から中身の core/model.ts の C
import type { PartState } from './types.js';

// 元に戻す・やり直すの1回分（history.ts）
export interface Snapshot { state: Record<string, PartState>; name: [string, string] }
export interface HistEntry { snap: Snapshot; key: string }
// 使い方の1手順（tour.ts。sel が配列なら全部をまとめて囲む）
export interface TourStep { sel?: string | string[]; title: string; text: string }
// グラデーションの今の色（colors.ts）
export interface GradState { key: string; from: string; to: string; sel: { from: boolean; to: boolean } }
// キラシールの受け渡し（kira.ts）
export interface KiraSession { win: Window; blob: Blob | null; ready: boolean; done: boolean }

export interface UiState {
  selKey: string;
  stageSvg: SVGSVGElement;
  moveMode: boolean;
  swatchCache: Record<string, string>;
  swatchGround: string;
  swatchDefs: SVGElement | null;
  grad: GradState | null;
  links: Record<string, string[]> | null;
  lrSel: number;
  colorTab: string;
  paletteSorted: string[] | null;
  observer: IntersectionObserver | null;
  gridOrder: string[];
  thumbRaf: number;
  thumbBatch: number;
  opaKey: string;
  boxRaf: number;
  boxFlash: number;
  layerKeyOf: Record<string, string> | null;
  view: { s: number; x: number; y: number };
  gestureTimer: number;
  toastTimer: number | null;
  undoSnap: Snapshot | null;
  hist: { base: HistEntry | null; past: HistEntry[]; future: HistEntry[]; timer: number; applying: boolean };
  autosaveReady: boolean;
  autosaveDirty: boolean;
  autosaveTimer: number;
  slotDbPromise: Promise<IDBDatabase> | null;
  nameText: SVGTextElement | null;
  nameFontAsked: boolean;
  finishNeedsReload: boolean;
  dlChoice: HTMLDivElement | null;
  kiraSession: KiraSession | null;
  modalOnClose: (() => void) | null;
  modalCls: string;
  agreedTour: boolean | null;
  appShown: boolean;
  tourSteps: TourStep[];
  tourIdx: number;
  bootWaitsAgree: boolean;
}

// stageSvg は起動（buildStage）で、selKey ははじめの selectCategory で入れてから使う
export const S: UiState = {
  selKey: null!,
  stageSvg: null!,
  moveMode: false, // 移動中（十字キーを出し、キャラの上のドラッグで動かす）
  // 柄の一覧の見本（1枚分の柄を、キャラと同じ makePattern で色を付けて画像に。今の色だけ取っておく）。
  // 色は計算後の値で見るので、ページの中の見えない svg の defs で作る
  swatchCache: {},
  swatchGround: '',
  swatchDefs: null,
  // colors.js
  // ---- 色 ----
  // グラデーション（髪・まゆ）: from = 上の色・to = 下の色（保存データの色From・色To。本物の setAllGradColor と同じくグループのカテゴリに）。
  //   「上の色・下の色」を切りかえて（左右の色と同じ形）パレットの色を押す。はじめは上。未設定なら上 = GRAD[1]・下 = GRAD[0]（パーツ本来の色）。
  //   ほかに上下の入れかえと、おすすめの2色（押すと上下まとめて）。
  //   本物はつまみを縦に動かして上→下の間の色も取れる（from = 補間(上, 下)(つまみの位置)）が、分かりにくいのでモックには無し
  //   （はじめはつまみの帯で作ったが、ユーザー判断で切りかえに。2026-10-11）
  grad: null, // { key, from, to, sel: { from, to } }（sel はどちらか一方）
  // colorlink.js
  links: null, // { hair: [キー…], skin: [キー…] }
  // lr.js
  // ---- 左右の色（IS_SEL）: 0 両方・1 左・2 右（本物の LR ボタン。カテゴリを選び直すと両方に戻る） ----
  lrSel: 0,
  // 色の段: 柄を付けられるカテゴリでは、先頭の「色・柄」で切りかえる（どちらを見ていたかは、カテゴリを替えても続ける）
  colorTab: 'color',
  // palette.js
  paletteSorted: null,
  // parts.js
  // ---- パーツ一覧（サムネイルは見えたときに作る） ----
  observer: null,
  gridOrder: [], // 開いた順（古いものから捨てる）
  thumbRaf: 0,
  // まとめて作る数。遅い端末では4つで 100ms を超えて止まり、その間のタップが待たされる → 1回が長くかかったら1つずつに（2026-10-18）
  thumbBatch: 4,
  // tools.js
  // うすさのつまみ: 「うすさ」のボタンの左に出す。動かしている間は place だけ（元に戻すは、さわり始めで区切る）。
  // 開いたまま別のカテゴリを選ぶ・ほかの調整を押す・外を押す・Esc で閉じる
  opaKey: '',
  // partbox.js
  // ---- 選んでいるパーツの枠・キャラの上を押して選ぶ ----
  // 移動中は、選んだカテゴリのパーツの見えている部分を枠で囲み、枠の中のドラッグで移動、四隅のつまみで大きさを変える
  // （大きさは「大きく・小さく」のボタンと同じ ±20%。本物の拡大はキャラの四角のまん中が基準なので、つまみではパーツのまん中が
  //  動かないように、その分を位置 tx・ty で戻す。保存データは今までどおり tx・ty・scale）。小さいパーツも、つかめる大きさの枠にする。
  // 移動中でないときは、キャラの上を押すと、そこにあるパーツのカテゴリを選ぶ（いちばん上のもの。選んでいるものをもう一度押すと、その下のもの）。
  // 選んだら、どれを選んだか枠を少しだけ出して知らせる
  boxRaf: 0,
  boxFlash: 0,
  layerKeyOf: null, // レイヤー名 → カテゴリのキー
  // view.js
  view: { s: 1, x: 0, y: 0 },
  gestureTimer: 0,
  // history.js
  // ---- お知らせ（「元に戻す」付き。ランダム・プリセットは確認を出さずに、これで戻せるようにする） ----
  toastTimer: null,
  undoSnap: null,
  hist: { base: null, past: [], future: [], timer: 0, applying: false },
  // storage.js
  autosaveReady: false,
  autosaveDirty: false,
  autosaveTimer: 0,
  // slots.js
  slotDbPromise: null,
  // name.js
  nameText: null,
  // 名前の書体を読む（1回だけ）。届いたら名前の幅を測り直す（届く前は別の書体の幅になっている）。
  // 前は起動のたびに読んでいた（名前が無くても）。名前があるとき・起動して少し後（完成のときに待たないように）に読む（2026-10-18）
  nameFontAsked: false,
  // finish.js
  // ---- 完成画面 ----
  // 画像（1000px・背景は透明・名前とコピーライトは canvas に描く）を作り、本物と同じ id の #chaMakeResultImageView に入れて完成画面を出す。
  // ボタン: ダウンロード（このファイル）・TOP ページにシェア＝お披露目（本物の additional.js の charatImgPublic）・このキャラでチャット
  //   （chibi/plaza/avatar.js の charatPlazaEnter。#chaSvgMake から作る）・キラシールにする（ORICA。storm と同じやりとり、このファイル）・
  //   高画質DL（additional.js の showModal。決済・注文番号・控えの保存はそのまま。画像は #chaSvgSave を 2000px で描く）
  // 閉じる: 本物は完成画面を閉じるとページを読み直す。モックはそのまま編集に戻り、お披露目・高画質DL のあとだけ読み直す
  //   （additional.js はお披露目を1回の読み込みで1回だけ受け付け、購入のあとは決済の部品を入れかえるため）
  finishNeedsReload: false,
  // download.js
  // 白い背景か透明な背景かを選ぶ画面（見た目は動いている間の「静止画か GIF か」と同じ .emote-dl。完成画面の中に出す）
  dlChoice: null,
  // kira.js
  kiraSession: null,
  // dialog.js
  // extras: 下の段の「閉じる」の前に足すボタン（閉じるときに外す）
  // opts.closeLabel: 閉じるボタンの文字（閉じるときに「閉じる」へ戻す）、opts.light: 後ろをぼかさず薄く暗くするだけ（キャラを見ながら変えるとき）
  // opts.onClose: 閉じたあとに1回だけ呼ぶ、opts.cls: モーダルに付けるクラス（閉じると外す）
  modalOnClose: null,
  modalCls: '',
  // agree.js
  // 画面ができる前に押されたら、できてから（読み込み中の画面が消えてから）
  agreedTour: window.CREATE_AGREED ? !!window.CREATE_AGREED.tour : null,
  appShown: false,
  // tour.js
  tourSteps: [],
  tourIdx: -1,
  // boot.js
  // はじめての人は、同意するまで読み込み中の画面のままにする（同意の画面の後ろで作る画面が現れると、ぼかしの描き直しでカクついていた。
  // 同意したら、読み込み中の画面が消えて作る画面が出る。2026-10-18）
  bootWaitsAgree: false,
};
